import React from 'react';
import { css, cx } from 'linaria';
import t from '@jetshop/intl';
import { ModalTrigger } from '@jetshop/ui/Modal/ModalTrigger';
import { theme } from '../Theme';
import { BaseHeading, H5, BaseText, MiniText } from '../ui/Typography';
import { ReactComponent as Close } from '../../svg/close.svg';

const sizeGuideButton = css`
  -webkit-appearance: none;
  -moz-appearance: none;
  width: max-content;
  font-size: 12px;
  font-weight: 400;
  line-height: 1;
  text-decoration: underline;
  color: #535353;
  background: transparent;
  border: 0;
  margin-left: 4px;
  text-transform: lowercase;
`;

const modalStyles = css`
  padding: 1rem;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
`;

const modalContent = css`
  padding: 3rem;
  background: ${theme.colors.white};
  height: max-content;
  width: 100%;
  max-width: 1200px;
  position: relative;

  ${theme.below.md} {
    max-width: 85%;
  }

  ${theme.below.sm} {
    padding: 1.875rem 1rem 3rem;
    max-width: 100%;
  }

  h3 {
    ${BaseHeading}
    font-size: 1.5rem;
    text-align: center;
    margin-bottom: 12px;

    ${theme.below.sm} {
      font-size: 1.125rem;
    }
  }

  span {
    display: block;
    text-align: center;
  }

  .flex {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    margin-top: 40px;

    ${theme.below.md} {
      flex-direction: column;
      margin-top: 14px;
    }
  }

  .col-one > * {
    width: 100%;
  }

  .col-two > * {
    width: 50%;

    width: 100%;
    ${theme.below.md} {
    }
  }

  .col-two aside {
    ${theme.below.md} {
      order: 1;
      margin-top: 1.5rem;
    }
  }

  .col-two table {
    ${theme.below.md} {
      margin-top: 0.875rem;
    }
  }

  aside {
    display: flex;
    flex-direction: column;
  }

  aside h5 {
    margin: 0;
  }

  aside img {
    padding-top: 1rem;
    padding-right: 1rem;
    width: 100%;
    max-width: max-content;
    margin: 0 auto;
    object-fit: scale-down;

    ${theme.below.md} {
      padding-top: 0.5rem;
      padding-right: 0;
    }
  }

  .table table {
    width: 100%;
  }

  .table thead tr th {
    ${BaseText};
    font-weight: ${theme.fontWeights.semibold};
    font-size: 0.75rem;
    color: ${theme.colors.black};

    padding: 0;
    padding-bottom: 1rem;
    line-height: 1;
    text-align: left;

    ${theme.below.md} {
      padding-bottom: 0.5rem;
    }
  }

  .table tbody tr:nth-child(odd) {
    background-color: ${theme.colors.offWhite};
  }

  .table tbody tr:nth-child(even) {
    background-color: ${theme.colors.white};
  }

  .table tbody tr td {
    ${BaseText};
    font-size: 0.75rem;
    color: ${theme.colors.text};

    padding: 8px;
    padding-left: 0;
    line-height: 1;
  }

  .table tbody tr td:first-of-type {
    font-weight: ${theme.fontWeights.semibold};
    color: ${theme.colors.black};
    padding-left: 12px;
  }
`;

const closeButton = css`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  width: 24px;
  height: 24px;
  line-height: 0;

  svg {
    width: 12px;
  }
`;

const SizeGuide = ({ product }) => {
  const sizingTable = product.customFields.find(
    f => f.key === 'sizeguide_table'
  )?.stringValue;

  if (!sizingTable) return null;

  return (
    <ModalTrigger
      modalStyles={modalStyles}
      target={props => <SizeGuideModal product={product} {...props} />}
    >
      {({ showTarget }) => (
        <button onClick={showTarget} className={sizeGuideButton}>
          {t('Size guide')}
        </button>
      )}
    </ModalTrigger>
  );
};

const SizeGuideModal = ({ product, hideTarget }) => {
  const sizingImage = product.customFields.find(
    customField => customField.key === 'sizeguide_image'
  )?.stringValue;
  const sizingFit = product.customFields.find(
    customField => customField.key === 'sizeguide_fit'
  )?.stringValue;
  const sizingTable = product.customFields.find(
    customField => customField.key === 'sizeguide_table'
  )?.stringValue;

  if (!sizingTable) return null;

  return (
    <section className={modalContent}>
      <button onClick={hideTarget} className={closeButton}>
        <Close />
      </button>
      <h3>{t('Size guide')}</h3>
      {sizingFit && <span className={MiniText}>{sizingFit}</span>}
      <div className={cx('flex', sizingImage ? 'col-two' : 'col-one')}>
        {sizingImage && (
          <aside>
            <H5>{t('How to measure')}</H5>
            <img src={sizingImage} alt="" />
          </aside>
        )}
        <div
          className="table"
          dangerouslySetInnerHTML={{ __html: sizingTable }}
        />
      </div>
    </section>
  );
};

export default SizeGuide;
